import React, { useReducer, useEffect, useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { BookingTransferForm } from '../BookingTransferForm/BookingTransferForm';
import { BookingCategoryList } from '../BookingCategoryList/BookingCategoryList';
import { BookingPaymentForm } from '../BookingPaymentForm/BookingPaymentForm';
import { BookingThankYou } from '../BookingThankYou';
import { BookingSuccess } from '../BookingSuccess';
import { bookingReducer, BOOKING_ACTION } from './bookingReducer';
import { bookingInitialValue } from './bookingInitialValue';
import { getIn } from '../../../common/utils/getIn';
import { transferFormValidationErrors } from '../BookingTransferForm/transferFormValidationErrors';
import { minBookingDate } from '../../../common/utils/date';
import { paymentFormValidationErrors } from '../BookingPaymentForm/paymentFormValidationErrors';
import { categoryListValidationErrors } from '../BookingCategoryList/categoryListValidationErrors';
import { detailsValidationErrors } from '../BookingPaymentForm/detailsValidationErrors';
import { syncServerClock } from '../../../common/utils/serverClock';
import { BookingNavigation } from '../BookingNavigation';
import { BookingDetailsForm } from '../BookingDetailsForm';
import {
    logAmplitudeEvent,
    onAmplitudeReady,
    isAmplitudeReady,
    captureUtmParameters,
} from '../../../common/services/amplitudeService';

const TrackedBookingStep = ({ stepName, component: Component, ...rest }) => {
    useEffect(() => {
        const trackStep = () => {
            logAmplitudeEvent('Funnel: Step Viewed', { step: stepName });
        };

        // If Amplitude is ready, track immediately
        if (isAmplitudeReady()) {
            trackStep();
        } else {
            // Otherwise, register callback for when it becomes ready
            onAmplitudeReady(trackStep);
        }
    }, [stepName]);

    return <Component {...rest} />;
};

const BookingPage = ({ user, setUser, notifications }) => {
    const [booking, setBooking] = useReducer(bookingReducer, bookingInitialValue);
    const navigate = useNavigate();
    const location = useLocation();
    const bookingPageUrl = '/booking';

    // Time-gated rules (express surcharge, earliest day, phone-only banner) must move on
    // without customer input. TICK re-evaluates the surcharge in state; the counter forces a
    // render even when nothing flipped (React skips it when the reducer returns the same
    // state), so the render-derived validation and banner pick up the new minute too.
    const [, setTick] = useState(0);
    const tick = () => {
        setBooking({ type: BOOKING_ACTION.TICK });
        setTick((t) => t + 1);
    };
    useEffect(() => {
        const sync = () => syncServerClock().then(tick);
        sync();
        const onVisible = () => document.visibilityState === 'visible' && sync();
        document.addEventListener('visibilitychange', onVisible);
        const intervalId = setInterval(tick, 60 * 1000);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(intervalId);
        };
    }, []);

    const firstTransfer = booking.transfers[0];
    const secondTransfer = booking.transfers[1];

    // Handle URL parameters for address initialization and UTM tracking
    useEffect(() => {
        // Capture UTM parameters for tracking attribution
        captureUtmParameters();

        const params = new URLSearchParams(location.search);
        const fromParam = params.get('from');
        const toParam = params.get('to');

        if (fromParam && toParam) {
            try {
                const addressFrom = JSON.parse(decodeURIComponent(fromParam));
                const addressTo = JSON.parse(decodeURIComponent(toParam));

                // Update the first transfer with the decoded addresses
                setBooking({
                    type: BOOKING_ACTION.UPDATE,
                    name: 'transfers.0.addressFrom',
                    value: addressFrom,
                });

                setBooking({
                    type: BOOKING_ACTION.UPDATE,
                    name: 'transfers.0.addressTo',
                    value: addressTo,
                });

                // Clean up the URL after processing parameters, keeping the rest (e.g. ?lang=en)
                params.delete('from');
                params.delete('to');
                navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
            } catch (error) {
                console.error('Error parsing address parameters:', error);
            }
        }
    }, [location.search, navigate, location.pathname]);

    const updateBookingUser = () => {
        setBooking({
            type: BOOKING_ACTION.UPDATE_USER,
            value: user,
        });
    };

    const updateBooking = ({ target, name, value }) => {
        if (target) {
            ({ name, value } = target);
        }

        setBooking({
            type: BOOKING_ACTION.UPDATE,
            name,
            value,
        });
    };

    const touchBooking = ({ target, name }) => {
        if (target) {
            ({ name } = target);
        }
        setBooking({
            type: BOOKING_ACTION.TOUCH,
            name,
        });
    };

    const [updateFirstTransfer, updateSecondTransfer] = [0, 1].map((index) => ({ target, name, value }) => {
        if (target) {
            ({ name, value } = target);
        }

        setBooking({
            type: BOOKING_ACTION.UPDATE,
            name: `transfers.${index}.${name}`,
            value,
        });
    });

    const [touchFirstTransfer, touchSecondTransfer] = [0, 1].map((index) => ({ target, name }) => {
        if (target) {
            ({ name } = target);
        }
        setBooking({
            type: BOOKING_ACTION.TOUCH,
            name: `transfers.${index}.${name}`,
        });
    });

    const bookingErrorSrc = (validationErrors, name) =>
        Boolean(getIn(booking.touched, name) && getIn(validationErrors, name));

    const [firstTransferErrorSrc, secondTransferErrorSrc] = [0, 1].map(
        (index) => (validationErrors, name) =>
            Boolean(
                getIn(booking.touched, `transfers.${index}.${name}`) &&
                getIn(validationErrors, `transfers.${index}.${name}`),
            ),
    );

    const resetBooking = () => {
        setBooking({ type: BOOKING_ACTION.RESET });
    };

    const showValidationErrors = (errors) => {
        setBooking({ type: BOOKING_ACTION.SHOW_ERRORS, value: errors });
    };

    const addTransfer = () => {
        setBooking({ type: BOOKING_ACTION.ADD_TRANSFER });
    };

    const removeTransfer = () => {
        setBooking({ type: BOOKING_ACTION.REMOVE_TRANSFER });
    };

    const data = {
        booking,
        firstTransfer,
        secondTransfer,
    };

    const update = {
        updateBooking,
        updateFirstTransfer,
        updateSecondTransfer,
        tickBooking: tick,
        resetBooking,
        addTransfer,
        removeTransfer,
    };

    const touch = {
        touchBooking,
        touchFirstTransfer,
        touchSecondTransfer,
    };

    const errorsSrc = {
        bookingErrorSrc,
        firstTransferErrorSrc,
        secondTransferErrorSrc,
    };

    const allValidationErrors = {
        transferForm: transferFormValidationErrors({
            firstTransfer,
            secondTransfer,
            minDate: minBookingDate(),
        }),
        categoryList: categoryListValidationErrors({ firstTransfer, secondTransfer }),
        paymentForm: paymentFormValidationErrors({ booking }),
        details: detailsValidationErrors({ booking }),
    };

    useEffect(updateBookingUser, [user]);

    // Smooth scroll to top when route changes (booking step changes)
    useEffect(() => {
        const scrollToTop = () => {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        };

        // Scroll to top immediately when component mounts or route changes
        scrollToTop();
    }, [location.pathname]);

    const validationRedirectCheck = (subPage) => {
        const category = [{ errors: allValidationErrors.transferForm, redirect: 'transfer' }];
        const details = [...category, { errors: allValidationErrors.categoryList, redirect: 'category' }];
        const payment = [...details, { errors: allValidationErrors.details, redirect: 'details' }];

        const map = { category, payment, details };

        const error = map[subPage].find((rule) => rule.errors);

        return (
            error && (
                <Navigate to={{ pathname: `${bookingPageUrl}/${error.redirect}`, search: location.search }} replace />
            )
        );
    };

    return (
        <Routes>
            <Route
                path="transfer"
                element={
                    <>
                        <BookingNavigation url={bookingPageUrl} allValidationErrors={allValidationErrors} />
                        <TrackedBookingStep
                            stepName="transfer"
                            component={BookingTransferForm}
                            url={bookingPageUrl}
                            data={data}
                            update={update}
                            touch={touch}
                            user={user}
                            errorsSrc={errorsSrc}
                            validationErrors={allValidationErrors.transferForm}
                            showValidationErrors={showValidationErrors}
                        />
                    </>
                }
            />
            <Route
                path="category"
                element={
                    <>
                        <BookingNavigation url={bookingPageUrl} allValidationErrors={allValidationErrors} />
                        {validationRedirectCheck('category') || (
                            <TrackedBookingStep
                                stepName="category"
                                component={BookingCategoryList}
                                url={bookingPageUrl}
                                data={data}
                                update={update}
                            />
                        )}
                    </>
                }
            />
            <Route
                path="details"
                element={
                    <>
                        <BookingNavigation url={bookingPageUrl} allValidationErrors={allValidationErrors} />
                        {validationRedirectCheck('details') || (
                            <TrackedBookingStep
                                stepName="details"
                                component={BookingDetailsForm}
                                data={data}
                                update={update}
                                url={bookingPageUrl}
                                touch={touch}
                                errorsSrc={errorsSrc}
                                validationErrors={allValidationErrors.details}
                                showValidationErrors={showValidationErrors}
                            />
                        )}
                    </>
                }
            />
            <Route
                path="payment"
                element={
                    <>
                        <BookingNavigation url={bookingPageUrl} allValidationErrors={allValidationErrors} />
                        {validationRedirectCheck('payment') || (
                            <TrackedBookingStep
                                stepName="payment"
                                component={BookingPaymentForm}
                                url={bookingPageUrl}
                                data={data}
                                update={update}
                                touch={touch}
                                errorsSrc={errorsSrc}
                                validationErrors={allValidationErrors.paymentForm}
                                showValidationErrors={showValidationErrors}
                                notifications={notifications}
                                user={user}
                                setUser={setUser}
                            />
                        )}
                    </>
                }
            />
            <Route path="thank-you" element={<BookingThankYou />} />
            <Route path="success" element={<BookingSuccess />} />
            <Route
                path="*"
                element={<Navigate to={{ pathname: `${bookingPageUrl}/transfer`, search: location.search }} replace />}
            />
        </Routes>
    );
};

export { BookingPage };
